<script setup>
import { ref, onMounted, onBeforeMount, onUnmounted, watch, computed, watchEffect } from "vue";
import Navbar from "@/components/common/navbars/Navbar.vue";
import SideNavbar from "@/components/common/navbars/SideNavbar.vue";
import Layout from "@/components/layouts/Layout.vue";
import UserPermissionMain from "@/components/userPermission/UserPermissionMain.vue";
import SessionExpiredModal from "@/components/common/modals/SessionExpiredModal.vue";
import { useGlobalStore } from '@/stores/global.js';
import { storeToRefs } from "pinia";
import { useCommunicatorStore } from '@/stores/communicator.js';
import { useRouter } from "vue-router";
import Footer from "@/components/common/footer/Footer.vue";

const communicatorStore = useCommunicatorStore();
const globalStore = useGlobalStore();
const global = storeToRefs(globalStore);
const { getSideNavKey: sideNavKey} = storeToRefs(global);
const sideBarKey = computed(() => sideNavKey.value);
const router = useRouter();
const showNotifications = ref(false);
const animateSidebarLine = ref(0);
const showModal = ref(false);
function closeModal() {
  router.push("/logout");
}
onBeforeMount(() => {
  window.addEventListener('resize', function() {
    globalStore.resetSideNavKey();
  });
});
function hideNotifications() {
  showNotifications.value = false;
}
function notificationsShown() {
  showNotifications.value = true;
}
function animateSidebar() {
  animateSidebarLine.value = Math.random() * 1000000;
}
const showExpirationModal = computed(() => {
  return communicatorStore.sessionHasExpired;
});
const disallowedRoutes = ['login', 'logout', 'register', 'invitation', 'activate', 'recoverAccount', 'acceptUser','passwordRecovery','brandLogin'];
watchEffect(() => {
  showModal.value = !disallowedRoutes.includes(router.currentRoute.value.name) && showExpirationModal.value;
});

// --- Fondo desktop post-login #EFF3F3 per a evitar espais en blanc ---
const APP_BG_CLASS = 'app-desktop-bg';
function applyPostLoginBackground(){
  const routeName = router.currentRoute.value.name;
  const isDesktop = window.innerWidth >= 1025; // breakpoint desktop
  if(isDesktop && routeName && !disallowedRoutes.includes(routeName)){ // Utilitzem les rutes dins de login
    document.body.classList.add(APP_BG_CLASS);
  } else {
    document.body.classList.remove(APP_BG_CLASS);
  }
}
onMounted(() => {
  applyPostLoginBackground();
  window.addEventListener('resize', applyPostLoginBackground);
});
watch(() => router.currentRoute.value.name, () => applyPostLoginBackground());
onUnmounted(() => {
  window.removeEventListener('resize', applyPostLoginBackground);
  document.body.classList.remove(APP_BG_CLASS);
});
</script>

<template>
  <Navbar 
    :key="sideBarKey"
    :showNotifications="showNotifications" 
    :animateToWorkfiles="animateSidebarLine"
    @notificationsShown="notificationsShown()" 
    @notificationsHidden="hideNotifications()"
  />
  <SideNavbar :key="sideBarKey" :animateToWorkfiles="animateSidebarLine"/>
  <Layout 
    :key="$route.fullPath" 
    @click="hideNotifications()" 
    @animateSidebar="animateSidebar"
  />
  <Footer :isModalActive="!showModal"/>
  <UserPermissionMain  />
  <SessionExpiredModal :show="showModal" @closeModal="closeModal"/>
</template>

<style>
@import "@/assets/base.css";

@media (max-width: 950px) and (orientation: landscape) {
  html, body, #app { min-height: 100vh; }
  @supports (height: -webkit-fill-available) {
    html, body, #app { min-height: -webkit-fill-available; }
  }
  @supports (height: 100dvh) {
    html, body, #app { min-height: 100dvh; }
  }
  @supports (height: 100lvh) {
    html, body, #app { min-height: 100lvh; }
  }
}

@supports (-webkit-touch-callout: none) {
  @media (max-width: 950px) and (orientation: landscape) {
    html, body, #app,
    .app__content,
    .wrapper,
    .no-wrapper {
      min-height: 100lvh !important;
    }
  }
}

body.app-desktop-bg {
  background-color: #EFF3F3 !important;
}

html {
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

body {
  height: 100%;
  width: 100%;
  margin: 0;
  padding: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

#app {
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  overflow-x: hidden;
}

/* Forcem l'ús de tot l'ample per a mòbil horitzontal */
@media (max-width: 950px) and (orientation: landscape) {
  html, body, #app {
    width: 100vw !important;
    max-width: 100vw !important;
    padding: 0 !important;
    margin: 0 !important;
    overflow-x: hidden !important;
    overflow-y: auto !important;
    box-sizing: border-box !important;
  }
  
  * {
    box-sizing: border-box;
  }
}

/* Assegurem el comportament correcte del scroll en Safari iOS */
@supports (-webkit-touch-callout: none) {
  #app {
    height: 100% !important;
    width: 100% !important;
    
    -webkit-overflow-scrolling: touch;
  }
}
.checkOut {
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
}
</style>
